import type { ReactElement } from "react";
import React, { createElement, useMemo } from "react";
import { Reka } from "@rekajs/core";
import { Parser } from "@rekajs/parser";
import * as t from "@rekajs/types";
import { observer } from "@rekajs/react";
import { Header } from "@repo/ui/header";
import { Footer } from "@repo/ui/footer";
import { WhatWeDo } from "@repo/ui/whatWeDo";
import { WhatWeDoItem } from "@repo/ui/whatWeDoItem";
import { Overflow2 } from "@repo/ui/overflow2";
import { ServicesCarousel } from "@repo/ui/servicesCarousel";
import { Container } from "@repo/ui/container";
import type { ComponentType } from "@repo/editor/types";
import type { Pages as PagesType } from "@repo/fetch-repository/pages";
import { PagesRepository } from "@repo/fetch-repository/pages";
import type { ErrorType } from "@repo/fetch-repository/factory";
import { Banner } from "@repo/ui/banner";
import { Text } from "@repo/ui/text";
import { Comments } from "@repo/ui/comments";
import { Timeline } from "@repo/ui/time-line";
import { TimelineItem } from "@repo/ui/time-line-item";
import { CatalogCarousel } from "@repo/ui/catalogCarousel";
import { ProjectGrid } from "@repo/ui/projectGrid";
import type { GetServerSidePropsContext } from "next";
import { componentSettingPropsToRekaProps } from "@repo/editor/externalComponents";
import { WorkWithUs } from "@repo/ui/workWithUsForm";
import { Space } from "@repo/ui/space";
import { TextImage } from "@repo/ui/textImage";
import { OverflowSection } from "@repo/ui/overflow";
import { ReviewHeader } from "@repo/ui/reviewHeader";
import { ReviewsList } from "@repo/ui/reviewsList";
import { ContactDetail } from "@repo/ui/contactDetail";
import { ContactDetailItem } from "@repo/ui/contactDetailItem";
import { ContactForm } from "@repo/ui/contactForm";
import { AboutCompanyList } from "@repo/ui/aboutCompanyList";
import { initializeStore } from "@repo/setting-store/provider";
import { SalesOffice } from "@repo/ui/SalesOffice";
import { DepartmentContacts } from "@repo/ui/DepartmentContacts";
import { DepartmentContactsItem } from "@repo/ui/DepartmentContactsItem";
import { Renderer } from "../components/editor/renderer";
import { Pure } from "../layouts/pure";

function registerExternalComponent(
  component: ComponentType<never>
): t.ExternalComponent {
  const name =
    component.componentSettings.name ||
    component.displayName ||
    `Component${Math.random() * 1000000}`;
  return t.externalComponent({
    name,
    props: componentSettingPropsToRekaProps(component.componentSettings.props),
    render: (props: undefined) => createElement(component, props),
  });
}

export async function getServerSideProps(
  context: GetServerSidePropsContext
): Promise<{
  props: {
    messages: Record<string, string>;
    pages: PagesType | null;
    pageError: ErrorType | null;
  };
  redirect?: {
    destination: string;
    permanent: boolean;
  };
}> {
  const store = initializeStore();
  if (!store.settingStore) {
    await store.fetchSettingStore(context.locale || "en");
  }

  const params = context.params as { params?: string[] };
  if (!params.params) {
    params.params = ["home"];
  }
  // Fetch data from external API
  const messages = (await import(`../locales/${context.locale}.json`)) as {
    default: Record<string, string>;
  };

  const repository: PagesRepository = new PagesRepository(context.locale);
  let pagesData = null;
  let pagesError: ErrorType | null = null;
  const res = await repository.get(params.params.join("/"), undefined);

  if (res.succeeded) {
    pagesData = res.data;
  } else {
    if (res.errors?.status === 404) {
      return {
        redirect: {
          destination: "/404",
          permanent: false,
        },
        props: {
          messages: messages.default,
          pages: null,
          pageError: null,
        },
      };
    }
    if (res.errors?.status === 500) {
      return {
        redirect: {
          destination: "/500",
          permanent: false,
        },
        props: {
          messages: messages.default,
          pages: null,
          pageError: null,
        },
      };
    }
    console.error(res.errors);
    pagesError = {
      message: res.errors?.message || null,
      status: res.errors?.status || null,
    };
  }

  return {
    props: {
      messages: messages.default,
      pages: pagesData,
      pageError: pagesError,
    },
  };
}

interface HomeProps {
  pages: PagesType | null;
  pageError: ErrorType | null;
}

function Home(props: HomeProps): React.JSX.Element {
  const reka = Reka.create({
    externals: {
      components: [
        ...[
          Header,
          Footer,
          Banner,
          CatalogCarousel,
          WorkWithUs,
          Space,
          TextImage,
          Container,
          OverflowSection,
          ServicesCarousel,
          WhatWeDo,
          WhatWeDoItem,
          ProjectGrid,
          Overflow2,
          ReviewsList,
          ReviewHeader,
          ContactForm,
          ContactDetail,
          ContactDetailItem,
          AboutCompanyList,
          Text,
          SalesOffice,
          DepartmentContacts,
          DepartmentContactsItem,
          Timeline,
          TimelineItem,
          Comments,
        ].map((component) => registerExternalComponent(component)),
      ],
    },
  });
  if (props.pages) {
    let program;
    if (props.pages.contents.length < 1) {
      program = Parser.parseProgram(`
 component App() => (
    <main>
    </main>
)
        `);
    } else {
      program = (JSON.parse(props.pages.contents) as t.State).program;
    }
    reka.load(
      t.state({
        extensions: {},
        program,
      })
    );

    reka.createFrame({
      id: "root",
      component: {
        name: "App",
      },
    });
  }

  const selectedFrame = useMemo(() => reka.frames[0], [reka.frames]);

  return props.pageError ? (
    <div>
      <h1>{props.pageError.status}</h1>
      <p>{props.pageError.message}</p>
    </div>
  ) : (
    <Renderer view={selectedFrame.view} />
  );
}

Home.getLayout = function getLayout(page: ReactElement) {
  return <Pure>{page}</Pure>;
};

export default observer(Home);
